import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
	ArrowLeft,
	Building2,
	ClipboardCheck,
	Clock,
	FileText,
	Loader2,
	Pencil,
	Plus,
	Save,
	Scale,
	Send,
	Target,
	Trash2,
	Upload,
	X,
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { useTRPC } from "@/integrations/trpc/react";
import { supabase } from "@/lib/supabase-browser";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { PdfFormEditorV2 } from "@/components/pdf-editor";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";

interface ContractPeriod {
	id: string;
	name: string;
	startDate: string;
	endDate: string;
}

export const Route = createFileRoute("/solicitations/new")({
	component: NewSolicitationPage,
});

function NewSolicitationPage() {
	const navigate = useNavigate();
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const { user } = useAuth();
	const [activeTab, setActiveTab] = useState("details");
	const [isPublishing, setIsPublishing] = useState(false);
	const [isSavingDraft, setIsSavingDraft] = useState(false);

	// tRPC mutations for forms
	const createTemplateMutation = useMutation(
		trpc.forms.templates.create.mutationOptions(),
	);

	const saveWidgetsMutation = useMutation(
		trpc.pdfTemplates.saveWidgets.mutationOptions(),
	);

	const addSolicitationFormMutation = useMutation(
		trpc.forms.solicitationForms.add.mutationOptions(),
	);

	const createSolicitationMutation = useMutation(
		trpc.solicitations.create.mutationOptions(),
	);
	const publishMutation = useMutation(
		trpc.solicitations.publish.mutationOptions(),
	);
	const saveContractPeriodsMutation = useMutation(
		trpc.solicitations.saveContractPeriods.mutationOptions(),
	);

	// Attachment mutations
	const createSignedUploadUrlMutation = useMutation(
		trpc.attachments.createSignedUploadUrl.mutationOptions(),
	);
	const uploadAttachmentMutation = useMutation(
		trpc.attachments.upload.mutationOptions(),
	);

	// Pending attachments state (files selected before solicitation is created)
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [pendingAttachments, setPendingAttachments] = useState<File[]>([]);
	const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (!files || files.length === 0) return;
		setPendingAttachments([...pendingAttachments, ...Array.from(files)]);
		// Reset the input so the same file can be selected again
		e.target.value = "";
	};

	const removePendingAttachment = (index: number) => {
		setPendingAttachments(pendingAttachments.filter((_, i) => i !== index));
	};

	// Helper function to upload pending attachments after solicitation is created
	const uploadPendingAttachments = async (solicitationId: string) => {
		if (pendingAttachments.length === 0) return;

		setIsUploadingAttachment(true);
		try {
			for (const file of pendingAttachments) {
				// Get signed upload URL
				const { signedUrl, path } = await createSignedUploadUrlMutation.mutateAsync({
					solicitationId,
					fileName: file.name,
				});

				// Upload file directly to storage
				const uploadResponse = await fetch(signedUrl, {
					method: "PUT",
					headers: {
						"Content-Type": file.type || "application/octet-stream",
					},
					body: file,
				});

				if (!uploadResponse.ok) {
					throw new Error(`Failed to upload ${file.name}`);
				}

				// Record attachment in database
				await uploadAttachmentMutation.mutateAsync({
					solicitationId,
					title: file.name,
					filePath: path,
					fileSize: file.size,
					mimeType: file.type || "application/octet-stream",
				});
			}
		} finally {
			setIsUploadingAttachment(false);
		}
	};

	const [formData, setFormData] = useState({
		title: "",
		description: "",
		type: "",
		publishDate: "",
		publishTime: "",
		dueDate: "",
		dueTime: "",
		qaDeadlineDate: "",
		qaDeadlineTime: "",
		memberOrgId: "",
		pointOfContactId: "",
	});

	// Get user's timezone for display
	const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	const timezoneAbbr = new Date().toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop() ?? userTimezone;

	// Helper to convert local date and time to UTC ISO string
	const localToUtcIso = (dateStr: string, timeStr: string) => {
		if (!dateStr) return undefined;
		const timeToUse = timeStr || "00:00";
		const localDate = new Date(`${dateStr}T${timeToUse}:00`);
		return localDate.toISOString();
	};

	// Fetch accessible organizations for the dropdown (includes buyer, member, and subtier orgs)
	const { data: accessibleOrgs } = useQuery(
		trpc.organizations.getAccessibleOrgs.queryOptions(),
	);

	// Fetch members for the point of contact dropdown
	// Uses listWithAccess to get members with inherited access from parent orgs
	const selectedOrgId = formData.memberOrgId || user?.organizationId || "";
	const { data: orgMembers } = useQuery(
		trpc.members.listWithAccess.queryOptions({
			organizationId: selectedOrgId,
		}),
	);

	// Fetch inherited forms from organization
	const { data: inheritedOrgForms } = useQuery(
		trpc.organizations.listOrgFormsWithInheritance.queryOptions(
			{ organizationId: selectedOrgId },
			{ enabled: !!selectedOrgId },
		),
	);

	const [milestones, setMilestones] = useState<Array<{
		title: string;
		date: string;
		time: string;
		isMeeting: boolean;
		isRequired: boolean;
		meetingAddress: string;
		meetingLink: string;
	}>>([]);

	// Contract periods state
	const [contractPeriods, setContractPeriods] = useState<ContractPeriod[]>([]);

	const handleAddContractPeriod = () => {
		const hasBaseContract = contractPeriods.some((p) => p.name.toLowerCase().includes("base"));
		const nextOptionYear =
			contractPeriods.filter((p) => p.name.includes("Option Year")).length + 1;
		setContractPeriods([
			...contractPeriods,
			{
				id: crypto.randomUUID(),
				name: contractPeriods.length === 0 || !hasBaseContract ? "Base Contract" : `Option Year ${nextOptionYear}`,
				startDate: "",
				endDate: "",
			},
		]);
	};

	const handleRemoveContractPeriod = (id: string) => {
		setContractPeriods(contractPeriods.filter((p) => p.id !== id));
	};

	const handleContractPeriodChange = (
		id: string,
		field: keyof ContractPeriod,
		value: string,
	) => {
		setContractPeriods(
			contractPeriods.map((p) => (p.id === id ? { ...p, [field]: value } : p)),
		);
	};

	// Form editor state
	interface SolicitationForm {
		id: string;
		name: string;
		description: string;
		required: boolean;
		pdfStoragePath: string | null;
		widgets: Array<{
			field_name: string;
			field_label: string;
			field_type: string;
			page_number: number;
			x: number;
			y: number;
			width: number;
			height: number;
			is_required: boolean;
			placeholder: string | null;
			description: string | null;
			options: string[] | null;
			font_size: number;
			tab_order: number;
		}>;
	}
	const [forms, setForms] = useState<SolicitationForm[]>([]);
	const [formEditorOpen, setFormEditorOpen] = useState(false);
	const [editingForm, setEditingForm] = useState<SolicitationForm | null>(null);
	const [newFormName, setNewFormName] = useState("");
	const [newFormDescription, setNewFormDescription] = useState("");
	const [newFormRequired, setNewFormRequired] = useState(true);

	// Required documents state (vendor uploads)
	interface RequiredDocument {
		id: string;
		name: string;
		description: string;
		required: boolean;
	}
	const [requiredDocuments, setRequiredDocuments] = useState<
		RequiredDocument[]
	>([]);
	const [newDocName, setNewDocName] = useState("");
	const [newDocDescription, setNewDocDescription] = useState("");
	const [newDocRequired, setNewDocRequired] = useState(true);

	// Compliance checks state
	interface ComplianceCheck {
		id: string;
		name: string;
		description: string;
		required: boolean;
		inherited: boolean; // true if inherited from org, false if solicitation-specific
	}

	// Simulated inherited compliance checks from the organization (would come from org settings in real app)
	const inheritedComplianceChecks: ComplianceCheck[] = [
		{
			id: "inherited-1",
			name: "SAM.gov Registration",
			description: "Must be registered in SAM.gov with active status",
			required: true,
			inherited: true,
		},
		{
			id: "inherited-2",
			name: "DUNS Number",
			description: "Valid DUNS number required",
			required: true,
			inherited: true,
		},
		{
			id: "inherited-3",
			name: "No Debarment",
			description: "Not currently debarred or suspended from federal contracts",
			required: true,
			inherited: true,
		},
	];

	const [solicitationComplianceChecks, setSolicitationComplianceChecks] =
		useState<ComplianceCheck[]>([]);
	const [newComplianceName, setNewComplianceName] = useState("");
	const [newComplianceDescription, setNewComplianceDescription] = useState("");
	const [newComplianceRequired, setNewComplianceRequired] = useState(true);

	const handleAddComplianceCheck = () => {
		if (!newComplianceName.trim()) {
			toast.error("Please enter a compliance check name");
			return;
		}
		setSolicitationComplianceChecks([
			...solicitationComplianceChecks,
			{
				id: crypto.randomUUID(),
				name: newComplianceName,
				description: newComplianceDescription,
				required: newComplianceRequired,
				inherited: false,
			},
		]);
		setNewComplianceName("");
		setNewComplianceDescription("");
		setNewComplianceRequired(true);
		toast.success("Compliance check added");
	};

	const handleAddRequiredDocument = () => {
		if (!newDocName.trim()) {
			toast.error("Please enter a document name");
			return;
		}
		setRequiredDocuments([
			...requiredDocuments,
			{
				id: crypto.randomUUID(),
				name: newDocName,
				description: newDocDescription,
				required: newDocRequired,
			},
		]);
		setNewDocName("");
		setNewDocDescription("");
		setNewDocRequired(true);
		toast.success("Required document added");
	};

	// Smart codes state - simple free text values
	const [smartCodes, setSmartCodes] = useState<string[]>([]);

	// Update smart codes mutation
	const updateSmartCodesMutation = useMutation(
		trpc.smartCodes.update.mutationOptions(),
	);

	// Update compliance checks mutation
	const updateComplianceChecksMutation = useMutation(
		trpc.complianceChecks.update.mutationOptions(),
	);

	// Required documents mutation
	const createRequiredDocMutation = useMutation(
		trpc.forms.requestedDocuments.create.mutationOptions(),
	);

	// Evaluation criteria state
	interface EvaluationCriterion {
		id: string;
		title: string;
		description: string;
		maxPoints: number;
		sortOrder: number;
	}

	const [criteria, setCriteria] = useState<EvaluationCriterion[]>([
		{
			id: "1",
			title: "Technical Approach",
			description: "Quality and feasibility of the proposed technical solution",
			maxPoints: 30,
			sortOrder: 1,
		},
		{
			id: "2",
			title: "Past Performance",
			description: "Demonstrated experience with similar projects",
			maxPoints: 25,
			sortOrder: 2,
		},
		{
			id: "3",
			title: "Price/Cost",
			description: "Competitiveness and reasonableness of pricing",
			maxPoints: 25,
			sortOrder: 3,
		},
		{
			id: "4",
			title: "Management Plan",
			description: "Project management approach and team qualifications",
			maxPoints: 20,
			sortOrder: 4,
		},
	]);

	// Scoring weights for criteria (maps criterion ID to weight percentage)
	const [criteriaWeights, setCriteriaWeights] = useState<Record<string, number>>({
		"1": 40,
		"2": 25,
		"3": 25,
		"4": 10,
	});

	const [editingCriterion, setEditingCriterion] =
		useState<EvaluationCriterion | null>(null);
	const [criterionTitle, setCriterionTitle] = useState("");
	const [criterionDescription, setCriterionDescription] = useState("");
	const [criterionPoints, setCriterionPoints] = useState(10);

	const totalPoints = criteria.reduce((sum, c) => sum + c.maxPoints, 0);
	const totalWeight = criteria.reduce((sum, c) => sum + (criteriaWeights[c.id] ?? 0), 0);

	// Initialize weights when criteria change
	useEffect(() => {
		if (criteria.length > 0) {
			const existingWeights = { ...criteriaWeights };
			let needsUpdate = false;

			// Add default weights for new criteria
			for (const criterion of criteria) {
				if (!(criterion.id in existingWeights)) {
					existingWeights[criterion.id] = Math.floor(100 / criteria.length);
					needsUpdate = true;
				}
			}

			// Remove weights for deleted criteria
			for (const id of Object.keys(existingWeights)) {
				if (!criteria.find(c => c.id === id)) {
					delete existingWeights[id];
					needsUpdate = true;
				}
			}

			if (needsUpdate) {
				setCriteriaWeights(existingWeights);
			}
		}
	}, [criteria]);

	const handleAddCriterion = () => {
		if (!criterionTitle.trim()) {
			toast.error("Please enter a criterion title");
			return;
		}
		const newCriterion: EvaluationCriterion = {
			id: crypto.randomUUID(),
			title: criterionTitle,
			description: criterionDescription,
			maxPoints: criterionPoints,
			sortOrder: criteria.length + 1,
		};
		setCriteria([...criteria, newCriterion]);
		setCriterionTitle("");
		setCriterionDescription("");
		setCriterionPoints(10);
		toast.success("Criterion added");
	};

	const handleUpdateCriterion = () => {
		if (!editingCriterion || !criterionTitle.trim()) return;
		setCriteria(
			criteria.map((c) =>
				c.id === editingCriterion.id
					? {
							...c,
							title: criterionTitle,
							description: criterionDescription,
							maxPoints: criterionPoints,
						}
					: c,
			),
		);
		setEditingCriterion(null);
		setCriterionTitle("");
		setCriterionDescription("");
		setCriterionPoints(10);
		toast.success("Criterion updated");
	};

	const handleDeleteCriterion = (id: string) => {
		setCriteria(criteria.filter((c) => c.id !== id));
		toast.success("Criterion removed");
	};

	const handleEditCriterion = (criterion: EvaluationCriterion) => {
		setEditingCriterion(criterion);
		setCriterionTitle(criterion.title);
		setCriterionDescription(criterion.description);
		setCriterionPoints(criterion.maxPoints);
	};

	const handleWeightChange = (id: string, value: number) => {
		setCriteriaWeights({ ...criteriaWeights, [id]: value });
	};

	const handleAddSmartCode = () => {
		setSmartCodes([...smartCodes, ""]);
	};

	const handleRemoveSmartCode = (index: number) => {
		if (smartCodes.length > 0) {
			setSmartCodes(smartCodes.filter((_, i) => i !== index));
		}
	};

	const handleSmartCodeChange = (index: number, value: string) => {
		const newCodes = [...smartCodes];
		newCodes[index] = value;
		setSmartCodes(newCodes);
	};

	const handleSaveDraft = async () => {
		if (!formData.title.trim()) {
			toast.error("Please enter a solicitation title");
			setActiveTab("details");
			return;
		}

		setIsSavingDraft(true);

		try {
			// Create the solicitation as a draft (status defaults to "draft")
			const solicitation = await createSolicitationMutation.mutateAsync({
				title: formData.title,
				description: formData.description || undefined,
				type: formData.type ? (formData.type as "RFP" | "RFQ" | "RFI" | "IFB" | "ITB") : "RFP",
				publishDate: localToUtcIso(formData.publishDate, formData.publishTime || "00:00"),
				dueDate: localToUtcIso(formData.dueDate, formData.dueTime || "23:59"),
				qaDeadline: localToUtcIso(formData.qaDeadlineDate, formData.qaDeadlineTime) ?? null,
				memberOrgId: formData.memberOrgId || undefined,
				pointOfContactId: formData.pointOfContactId || undefined,
				buyerOrgId: user?.organizationId ?? "",
			});

			// Save smart codes if any
			const validSmartCodes = smartCodes.filter((sc) => sc.trim());
			if (validSmartCodes.length > 0) {
				await updateSmartCodesMutation.mutateAsync({
					solicitationId: solicitation.id,
					values: validSmartCodes,
				});
			}

			// Save solicitation-specific compliance checks if any
			const validChecks = solicitationComplianceChecks
				.filter((c) => c.name.trim())
				.map((c, index) => ({
					title: c.name,
					description: c.description || null,
					isRequired: c.required,
					sortOrder: index,
				}));
			if (validChecks.length > 0) {
				await updateComplianceChecksMutation.mutateAsync({
					solicitationId: solicitation.id,
					checks: validChecks,
				});
			}

			// Save solicitation-specific required documents if any
			for (const doc of requiredDocuments) {
				if (doc.name.trim()) {
					await createRequiredDocMutation.mutateAsync({
						solicitationId: solicitation.id,
						title: doc.name,
						description: doc.description || null,
						isRequired: doc.required,
						sortOrder: requiredDocuments.indexOf(doc),
					});
				}
			}

			// Save contract periods
			const periodsToSave = contractPeriods
				.filter((p) => p.name.trim())
				.map((p, index) => ({
					id: undefined, // New periods don't have IDs yet
					name: p.name,
					startDate: p.startDate || null,
					endDate: p.endDate || null,
					sortOrder: index,
				}));
			if (periodsToSave.length > 0) {
				await saveContractPeriodsMutation.mutateAsync({
					solicitationId: solicitation.id,
					periods: periodsToSave,
				});
			}

			// Save forms with their widgets
			for (let i = 0; i < forms.length; i++) {
				const form = forms[i];
				if (form.pdfStoragePath) {
					// Create form template with Nutrient document ID
					const template = await createTemplateMutation.mutateAsync({
						title: form.name,
						description: form.description || undefined,
						pdfStoragePath: form.pdfStoragePath,
						fieldConfig: [],
					});

					// Save widgets to the template if any were defined
					if (form.widgets.length > 0) {
						await saveWidgetsMutation.mutateAsync({
							templateId: template.id,
							widgets: form.widgets,
						});
					}

					// Link form to solicitation
					await addSolicitationFormMutation.mutateAsync({
						solicitationId: solicitation.id,
						formTemplateId: template.id,
						title: form.name,
						description: form.description || undefined,
						isRequired: form.required,
						sortOrder: i,
					});
				}
			}

			// Upload any pending attachments
			await uploadPendingAttachments(solicitation.id);

			// Invalidate the solicitations list query to show the new draft
			queryClient.invalidateQueries({
				queryKey: trpc.solicitations.list.queryKey(),
			});

			toast.success("Draft saved successfully!");

			// Navigate to edit the draft
			navigate({ to: "/solicitations/$id/edit", params: { id: solicitation.id } });
		} catch (error) {
			console.error("Error saving draft:", error);
			toast.error(error instanceof Error ? error.message : "Failed to save draft");
		} finally {
			setIsSavingDraft(false);
		}
	};

	const handlePublish = async () => {
		if (!formData.title.trim()) {
			toast.error("Please enter a solicitation title");
			setActiveTab("details");
			return;
		}
		if (!formData.type) {
			toast.error("Please select a solicitation type");
			setActiveTab("details");
			return;
		}

		setIsPublishing(true);

		try {
			// 1. Create the solicitation
			const solicitation = await createSolicitationMutation.mutateAsync({
				title: formData.title,
				description: formData.description || undefined,
				type: formData.type as "RFP" | "RFQ" | "RFI" | "IFB" | "ITB",
				publishDate: localToUtcIso(formData.publishDate, formData.publishTime || "00:00") ?? new Date().toISOString(),
				dueDate: localToUtcIso(formData.dueDate, formData.dueTime || "23:59"),
				qaDeadline: localToUtcIso(formData.qaDeadlineDate, formData.qaDeadlineTime) ?? null,
				memberOrgId: formData.memberOrgId || undefined,
				pointOfContactId: formData.pointOfContactId || undefined,
				buyerOrgId: user?.organizationId ?? "",
			});

			// 2. For each form, create form template + solicitation form
			// PDFs are already uploaded to Nutrient cloud, we just need to store the document ID
			for (let i = 0; i < forms.length; i++) {
				const form = forms[i];
				if (form.pdfStoragePath) {
					// Create form template with Nutrient document ID
					const template = await createTemplateMutation.mutateAsync({
						title: form.name,
						description: form.description || undefined,
						pdfStoragePath: form.pdfStoragePath,
						fieldConfig: [],
					});

					// Save widgets to the template if any were defined
					if (form.widgets.length > 0) {
						await saveWidgetsMutation.mutateAsync({
							templateId: template.id,
							widgets: form.widgets,
						});
					}

					// Link form to solicitation
					await addSolicitationFormMutation.mutateAsync({
						solicitationId: solicitation.id,
						formTemplateId: template.id,
						title: form.name,
						description: form.description || undefined,
						isRequired: form.required,
						sortOrder: i,
					});
				}
			}

			// 3. Save smart codes if any
			const validSmartCodes = smartCodes.filter((sc) => sc.trim());
			if (validSmartCodes.length > 0) {
				await updateSmartCodesMutation.mutateAsync({
					solicitationId: solicitation.id,
					values: validSmartCodes,
				});
			}

			// 4. Save solicitation-specific compliance checks if any
			const validChecks = solicitationComplianceChecks
				.filter((c) => c.name.trim())
				.map((c, index) => ({
					title: c.name,
					description: c.description || null,
					isRequired: c.required,
					sortOrder: index,
				}));
			if (validChecks.length > 0) {
				await updateComplianceChecksMutation.mutateAsync({
					solicitationId: solicitation.id,
					checks: validChecks,
				});
			}

			// 5. Save solicitation-specific required documents if any
			for (const doc of requiredDocuments) {
				if (doc.name.trim()) {
					await createRequiredDocMutation.mutateAsync({
						solicitationId: solicitation.id,
						title: doc.name,
						description: doc.description || null,
						isRequired: doc.required,
						sortOrder: requiredDocuments.indexOf(doc),
					});
				}
			}

			// 6. Save contract periods
			const periodsToSave = contractPeriods
				.filter((p) => p.name.trim())
				.map((p, index) => ({
					id: undefined, // New periods don't have IDs yet
					name: p.name,
					startDate: p.startDate || null,
					endDate: p.endDate || null,
					sortOrder: index,
				}));
			if (periodsToSave.length > 0) {
				await saveContractPeriodsMutation.mutateAsync({
					solicitationId: solicitation.id,
					periods: periodsToSave,
				});
			}

			// 7. Upload any pending attachments
			await uploadPendingAttachments(solicitation.id);

			// 8. Publish the solicitation (sets status to "open")
			// If publish date is in the future, it becomes "scheduled" (virtual state)
			const publishDateIso = localToUtcIso(formData.publishDate, formData.publishTime || "00:00");
			await publishMutation.mutateAsync({
				id: solicitation.id,
				publishDate: publishDateIso,
			});

			// Invalidate queries to refresh data
			queryClient.invalidateQueries({ queryKey: trpc.solicitations.list.queryKey() });

			// Show appropriate message based on whether it's scheduled or published
			const isScheduled = publishDateIso && new Date(publishDateIso) > new Date();
			toast.success(isScheduled ? "Solicitation scheduled successfully!" : "Solicitation published successfully!");
			navigate({ to: "/solicitations" });
		} catch (error) {
			console.error("Error publishing solicitation:", error);
			toast.error(error instanceof Error ? error.message : "Failed to publish solicitation");
		} finally {
			setIsPublishing(false);
		}
	};

	const addMilestone = () => {
		setMilestones([
			...milestones,
			{
				title: "",
				date: "",
				time: "",
				isMeeting: false,
				isRequired: false,
				meetingAddress: "",
				meetingLink: "",
			},
		]);
	};

	const removeMilestone = (index: number) => {
		setMilestones(milestones.filter((_, i) => i !== index));
	};

	return (
		<DashboardLayout>
			<div className="space-y-6">
				{/* Header */}
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-4">
						<Button variant="ghost" size="icon" asChild>
							<Link to="/solicitations">
								<ArrowLeft className="h-4 w-4" />
							</Link>
						</Button>
						<div>
							<h1 className="text-3xl font-bold tracking-tight">
								New Solicitation
							</h1>
							<p className="text-muted-foreground">
								Create a new procurement solicitation
							</p>
						</div>
					</div>
					<div className="flex gap-2">
						<Button variant="outline" onClick={handleSaveDraft} disabled={isPublishing || isSavingDraft}>
							{isSavingDraft ? (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							) : (
								<Save className="mr-2 h-4 w-4" />
							)}
							{isSavingDraft ? "Saving..." : "Save Draft"}
						</Button>
						{(() => {
							const publishDateIso = localToUtcIso(formData.publishDate, formData.publishTime);
							const isScheduled = publishDateIso && new Date(publishDateIso) > new Date();
							return (
								<Button onClick={handlePublish} disabled={isPublishing || isSavingDraft}>
									{isPublishing ? (
										<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									) : isScheduled ? (
										<Clock className="mr-2 h-4 w-4" />
									) : (
										<Send className="mr-2 h-4 w-4" />
									)}
									{isPublishing ? (isScheduled ? "Scheduling..." : "Publishing...") : (isScheduled ? "Schedule" : "Publish")}
								</Button>
							);
						})()}
					</div>
				</div>

				{/* Form Tabs */}
				<Tabs value={activeTab} onValueChange={setActiveTab}>
					<TabsList>
						<TabsTrigger value="details">Basic Details</TabsTrigger>
						<TabsTrigger value="dates">Dates & Milestones</TabsTrigger>
						<TabsTrigger value="attachments">Attachments</TabsTrigger>
						<TabsTrigger value="requirements">Requirements</TabsTrigger>
						<TabsTrigger value="evaluation">Evaluation</TabsTrigger>
					</TabsList>

					<TabsContent value="details" className="space-y-6">
						<Card>
							<CardHeader>
								<CardTitle>Solicitation Information</CardTitle>
								<CardDescription>
									Basic information about the solicitation
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="grid gap-4 md:grid-cols-2">
									<div className="space-y-2 md:col-span-2">
										<Label htmlFor="title">Title *</Label>
										<Input
											id="title"
											placeholder="Enter solicitation title"
											value={formData.title}
											onChange={(e) =>
												setFormData({ ...formData, title: e.target.value })
											}
										/>
									</div>

									<div className="space-y-2">
										<Label htmlFor="type">Solicitation Type *</Label>
										<Select
											value={formData.type}
											onValueChange={(value) =>
												setFormData({ ...formData, type: value })
											}
										>
											<SelectTrigger>
												<SelectValue placeholder="Select type" />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="RFP">
													Request for Proposal (RFP)
												</SelectItem>
												<SelectItem value="RFQ">
													Request for Quote (RFQ)
												</SelectItem>
												<SelectItem value="RFI">
													Request for Information (RFI)
												</SelectItem>
												<SelectItem value="IFB">
													Invitation for Bid (IFB)
												</SelectItem>
												<SelectItem value="ITB">
													Invitation to Bid (ITB)
												</SelectItem>
											</SelectContent>
										</Select>
									</div>

									<div className="space-y-2 md:col-span-2">
										<Label htmlFor="description">Description *</Label>
										<Textarea
											id="description"
											placeholder="Provide a detailed description of the solicitation"
											rows={5}
											value={formData.description}
											onChange={(e) =>
												setFormData({
													...formData,
													description: e.target.value,
												})
											}
										/>
									</div>
								</div>
							</CardContent>
						</Card>

						<Card>
							<CardHeader>
								<CardTitle>Organization & Contact</CardTitle>
								<CardDescription>
									Select the department and point of contact for this solicitation
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="grid gap-4 md:grid-cols-2">
									<div className="space-y-2">
										<Label htmlFor="memberOrg">Department / Member Org</Label>
										<Select
											value={formData.memberOrgId}
											onValueChange={(value) =>
												setFormData({ ...formData, memberOrgId: value, pointOfContactId: "" })
											}
										>
											<SelectTrigger>
												<SelectValue placeholder="Select department (optional)" />
											</SelectTrigger>
											<SelectContent>
												{(accessibleOrgs ?? []).map((org) => (
													<SelectItem key={org.id} value={org.id}>
														{org.name}
														{org.type !== "buyer" && (
															<span className="text-muted-foreground ml-1">
																({org.type})
															</span>
														)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<p className="text-xs text-muted-foreground">
											Optionally assign this solicitation to a specific department
										</p>
									</div>

									<div className="space-y-2">
										<Label htmlFor="pointOfContact">Point of Contact</Label>
										<Select
											value={formData.pointOfContactId}
											onValueChange={(value) =>
												setFormData({ ...formData, pointOfContactId: value })
											}
										>
											<SelectTrigger>
												<SelectValue placeholder="Select point of contact" />
											</SelectTrigger>
											<SelectContent>
												{(orgMembers ?? []).map((member) => (
													<SelectItem key={member.user_id} value={member.user_id}>
														{member.profiles?.first_name} {member.profiles?.last_name}
														{member.profiles?.email && (
															<span className="text-muted-foreground ml-1">
																({member.profiles.email})
															</span>
														)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<p className="text-xs text-muted-foreground">
											The person vendors can contact with questions
										</p>
									</div>
								</div>
							</CardContent>
						</Card>

						<Card>
							<CardHeader>
								<CardTitle>Smart Codes</CardTitle>
								<CardDescription>
									Add categorization codes for this solicitation
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="space-y-3">
									{smartCodes.map((smartCode, index) => (
										<div key={index} className="flex items-center gap-2">
											<Input
												placeholder="e.g., 541512 - Computer Systems Design Services"
												value={smartCode}
												onChange={(e) =>
													handleSmartCodeChange(index, e.target.value)
												}
												className="flex-1"
											/>
											<Button
												type="button"
												variant="ghost"
												size="icon"
												onClick={() => handleRemoveSmartCode(index)}
											>
												<X className="h-4 w-4" />
											</Button>
										</div>
									))}
								</div>
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={handleAddSmartCode}
								>
									<Plus className="mr-2 h-4 w-4" />
									Add Smart Code
								</Button>
								<p className="text-sm text-muted-foreground">
									Enter classification codes for this solicitation.
								</p>
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="dates" className="space-y-6">
						<Card>
							<CardHeader>
								<CardTitle>Key Dates</CardTitle>
								<CardDescription>
									Set the publish and due dates for this solicitation. All times shown in your local timezone ({timezoneAbbr}).
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="grid gap-4 md:grid-cols-2">
									<div className="space-y-2">
										<Label htmlFor="publishDate">Publish Date</Label>
										<Input
											id="publishDate"
											type="date"
											value={formData.publishDate}
											onChange={(e) =>
												setFormData({
													...formData,
													publishDate: e.target.value,
												})
											}
										/>
										<Label htmlFor="publishTime">Publish Time</Label>
										<Input
											id="publishTime"
											type="time"
											value={formData.publishTime}
											onChange={(e) =>
												setFormData({
													...formData,
													publishTime: e.target.value,
												})
											}
										/>
										<p className="text-sm text-muted-foreground">
											Leave blank to publish immediately, or set a future date
											for scheduled publishing
										</p>
									</div>

									<div className="space-y-2">
										<Label htmlFor="dueDate">Due Date *</Label>
										<Input
											id="dueDate"
											type="date"
											value={formData.dueDate}
											onChange={(e) =>
												setFormData({ ...formData, dueDate: e.target.value })
											}
										/>
										<Label htmlFor="dueTime">Due Time *</Label>
										<Input
											id="dueTime"
											type="time"
											value={formData.dueTime}
											onChange={(e) =>
												setFormData({ ...formData, dueTime: e.target.value })
											}
										/>
										<p className="text-sm text-muted-foreground">
											The deadline for vendors to submit their bids
										</p>
									</div>
								</div>
								<div className="grid gap-4 md:grid-cols-2 mt-4">
									<div className="space-y-2">
										<Label htmlFor="qaDeadlineDate">Q&A Deadline (Optional)</Label>
										<Input
											id="qaDeadlineDate"
											type="date"
											value={formData.qaDeadlineDate}
											onChange={(e) =>
												setFormData({
													...formData,
													qaDeadlineDate: e.target.value,
												})
											}
										/>
										<Label htmlFor="qaDeadlineTime">Q&A Deadline Time</Label>
										<Input
											id="qaDeadlineTime"
											type="time"
											value={formData.qaDeadlineTime}
											onChange={(e) =>
												setFormData({
													...formData,
													qaDeadlineTime: e.target.value,
												})
											}
										/>
										<p className="text-xs text-muted-foreground">
											Vendors can only submit questions before this deadline
										</p>
									</div>
									<div />
								</div>
							</CardContent>
						</Card>

						<Card>
							<CardHeader className="flex flex-row items-center justify-between">
								<div>
									<CardTitle>Contract Periods</CardTitle>
									<CardDescription>
										Define performance periods for this contract (optional)
									</CardDescription>
								</div>
								<Button variant="outline" onClick={handleAddContractPeriod}>
									<Plus className="mr-2 h-4 w-4" />
									Add Period
								</Button>
							</CardHeader>
							<CardContent className="space-y-4">
								{contractPeriods.length === 0 ? (
									<p className="text-sm text-muted-foreground text-center py-4">
										No contract periods defined. Click "Add Period" to add one.
									</p>
								) : contractPeriods.map((period) => (
									<div
										key={period.id}
										className="flex items-start gap-4 p-4 border rounded-lg"
									>
										<div className="flex-1 space-y-4">
											<div className="grid gap-4 md:grid-cols-3">
												<div className="space-y-2">
													<Label>Period Name</Label>
													<Input
														placeholder="e.g., Base Contract"
														value={period.name}
														onChange={(e) =>
															handleContractPeriodChange(
																period.id,
																"name",
																e.target.value,
															)
														}
													/>
												</div>
												<div className="space-y-2">
													<Label>Start Date</Label>
													<Input
														type="date"
														value={period.startDate}
														onChange={(e) =>
															handleContractPeriodChange(
																period.id,
																"startDate",
																e.target.value,
															)
														}
													/>
												</div>
												<div className="space-y-2">
													<Label>End Date</Label>
													<Input
														type="date"
														value={period.endDate}
														onChange={(e) =>
															handleContractPeriodChange(
																period.id,
																"endDate",
																e.target.value,
															)
														}
													/>
												</div>
											</div>
										</div>
										<Button
											variant="ghost"
											size="icon"
											onClick={() => handleRemoveContractPeriod(period.id)}
										>
											<X className="h-4 w-4" />
										</Button>
									</div>
								))}
							</CardContent>
						</Card>

						<Card>
							<CardHeader className="flex flex-row items-center justify-between">
								<div>
									<CardTitle>Custom Milestones</CardTitle>
									<CardDescription>
										Add additional milestones like Q&A deadlines or pre-bid
										conferences. Times shown in {timezoneAbbr}.
									</CardDescription>
								</div>
								<Button variant="outline" onClick={addMilestone}>
									<Plus className="mr-2 h-4 w-4" />
									Add Milestone
								</Button>
							</CardHeader>
							<CardContent className="space-y-4">
								{milestones.map((milestone, index) => (
									<div
										key={index}
										className="flex items-start gap-4 p-4 border rounded-lg"
									>
										<div className="flex-1 space-y-4">
											<div className="grid gap-4 md:grid-cols-3">
												<div className="space-y-2">
													<Label>Title</Label>
													<Input
														placeholder="Milestone title"
														value={milestone.title}
														onChange={(e) => {
															const updated = [...milestones];
															updated[index].title = e.target.value;
															setMilestones(updated);
														}}
													/>
												</div>
												<div className="space-y-2">
													<Label>Date</Label>
													<Input
														type="date"
														value={milestone.date}
														onChange={(e) => {
															const updated = [...milestones];
															updated[index].date = e.target.value;
															setMilestones(updated);
														}}
													/>
													<Label>Time</Label>
													<Input
														type="time"
														value={milestone.time}
														onChange={(e) => {
															const updated = [...milestones];
															updated[index].time = e.target.value;
															setMilestones(updated);
														}}
													/>
												</div>
												<div className="space-y-2">
													<Label>Type</Label>
													<div className="flex items-center gap-2 pt-2">
														<Checkbox
															checked={milestone.isMeeting}
															onCheckedChange={(checked) => {
																const updated = [...milestones];
																updated[index].isMeeting = checked as boolean;
																setMilestones(updated);
															}}
														/>
														<span className="text-sm">This is a meeting</span>
													</div>
												</div>
											</div>
											{milestone.isMeeting && (
												<div className="grid gap-4 md:grid-cols-3 pl-4 border-l-2 border-muted">
													<div className="space-y-2">
														<div className="flex items-center gap-2">
															<Checkbox
																checked={milestone.isRequired}
																onCheckedChange={(checked) => {
																	const updated = [...milestones];
																	updated[index].isRequired =
																		checked as boolean;
																	setMilestones(updated);
																}}
															/>
															<Label className="font-normal">
																Attendance Required
															</Label>
														</div>
													</div>
													<div className="space-y-2">
														<Label>Meeting Address</Label>
														<Input
															placeholder="123 Main St, City, State"
															value={milestone.meetingAddress}
															onChange={(e) => {
																const updated = [...milestones];
																updated[index].meetingAddress = e.target.value;
																setMilestones(updated);
															}}
														/>
													</div>
													<div className="space-y-2">
														<Label>Meeting Link</Label>
														<Input
															placeholder="https://zoom.us/j/..."
															value={milestone.meetingLink}
															onChange={(e) => {
																const updated = [...milestones];
																updated[index].meetingLink = e.target.value;
																setMilestones(updated);
															}}
														/>
													</div>
												</div>
											)}
										</div>
										<Button
											variant="ghost"
											size="icon"
											onClick={() => removeMilestone(index)}
										>
											<X className="h-4 w-4" />
										</Button>
									</div>
								))}
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="attachments" className="space-y-6">
						<Card>
							<CardHeader>
								<CardTitle>Solicitation Attachments</CardTitle>
								<CardDescription>
									Reference documents for vendors ({pendingAttachments.length} files)
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								{/* Hidden file input */}
								<input
									ref={fileInputRef}
									type="file"
									multiple
									accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
									className="hidden"
									onChange={handleFileSelect}
								/>

								{/* Upload zone */}
								<div
									className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:bg-muted/50 transition-colors"
									onClick={() => fileInputRef.current?.click()}
								>
									{isUploadingAttachment ? (
										<>
											<Loader2 className="h-10 w-10 mx-auto text-muted-foreground mb-4 animate-spin" />
											<p className="font-medium">Uploading...</p>
										</>
									) : (
										<>
											<Upload className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
											<p className="font-medium">
												Click to upload or drag and drop
											</p>
											<p className="text-sm text-muted-foreground">
												PDF, DOC, DOCX, XLS, XLSX up to 500MB
											</p>
										</>
									)}
								</div>

								{/* Pending attachments list */}
								{pendingAttachments.length > 0 ? (
									<div className="space-y-3">
										<Label>Files to Upload ({pendingAttachments.length})</Label>
										{pendingAttachments.map((file, index) => (
											<div
												key={`${file.name}-${index}`}
												className="flex items-center justify-between p-4 border rounded-lg"
											>
												<div className="flex items-center gap-3">
													<FileText className="h-5 w-5 text-muted-foreground" />
													<div>
														<p className="font-medium">{file.name}</p>
														<p className="text-sm text-muted-foreground">
															{(file.size / 1024 / 1024).toFixed(2)} MB
															{file.type && ` • ${file.type.split("/")[1]?.toUpperCase() || file.type}`}
														</p>
													</div>
												</div>
												<Button
													variant="ghost"
													size="icon"
													onClick={() => removePendingAttachment(index)}
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</div>
										))}
										<p className="text-sm text-muted-foreground">
											Files will be uploaded when you save the draft or publish.
										</p>
									</div>
								) : (
									<div className="text-center py-4 text-muted-foreground">
										<p>No attachments selected yet.</p>
									</div>
								)}
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="requirements" className="space-y-6">
						{/* Vendor Forms */}
						<Card>
							<CardHeader>
								<CardTitle>Vendor Forms</CardTitle>
								<CardDescription>
									Fillable PDF forms that vendors must complete during bid
									submission
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								{/* Add new form section */}
								<div className="border rounded-lg p-4 space-y-4 bg-muted/30">
									<h4 className="font-medium">Add New Form</h4>
									<div className="grid gap-4 md:grid-cols-2">
										<div className="space-y-2">
											<Label>Form Title *</Label>
											<Input
												placeholder="e.g., Vendor Information Form"
												value={newFormName}
												onChange={(e) => setNewFormName(e.target.value)}
											/>
										</div>
										<div className="space-y-2">
											<Label>Required</Label>
											<div className="flex items-center gap-2 pt-2">
												<Checkbox
													checked={newFormRequired}
													onCheckedChange={(checked) =>
														setNewFormRequired(checked as boolean)
													}
												/>
												<span className="text-sm">
													Vendors must complete this form
												</span>
											</div>
										</div>
									</div>
									<div className="space-y-2">
										<Label>Description</Label>
										<Textarea
											placeholder="Describe what this form is for..."
											value={newFormDescription}
											onChange={(e) => setNewFormDescription(e.target.value)}
											rows={2}
										/>
									</div>
									<Button
										onClick={() => {
											if (!newFormName.trim()) {
												toast.error("Please enter a form title");
												return;
											}
											setEditingForm(null);
											setFormEditorOpen(true);
										}}
									>
										<Plus className="mr-2 h-4 w-4" />
										Create Form PDF
									</Button>
									<p className="text-xs text-muted-foreground">
										Upload a PDF and add fillable form fields for vendor
										responses.
									</p>
								</div>

								{/* Inherited forms from organization */}
								{inheritedOrgForms && inheritedOrgForms.length > 0 && (
									<div className="space-y-3">
										<div className="flex items-center gap-2">
											<Building2 className="h-4 w-4 text-muted-foreground" />
											<Label className="text-muted-foreground">
												Inherited from Organization ({inheritedOrgForms.length})
											</Label>
										</div>
										{inheritedOrgForms.map((form) => (
											<div
												key={form.id}
												className="flex items-start justify-between p-4 border rounded-lg bg-muted/20"
											>
												<div className="flex items-start gap-3">
													<FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
													<div>
														<div className="flex items-center gap-2 flex-wrap">
															<span className="font-medium">{form.title}</span>
															<Badge
																variant={form.is_required ? "default" : "outline"}
															>
																{form.is_required ? "Required" : "Optional"}
															</Badge>
															{form.sourceOrganizationName && (
																<Badge variant="secondary">
																	From: {form.sourceOrganizationName}
																</Badge>
															)}
														</div>
														{form.description && (
															<p className="text-sm text-muted-foreground mt-1">
																{form.description}
															</p>
														)}
													</div>
												</div>
											</div>
										))}
									</div>
								)}

								{/* Existing forms list (solicitation-specific) */}
								{forms.length > 0 && (
									<div className="space-y-3 mt-6">
										<Label>
											{inheritedOrgForms && inheritedOrgForms.length > 0
												? `Solicitation-Specific Forms (${forms.length})`
												: `Created Forms (${forms.length})`}
										</Label>
										{forms.map((form) => (
											<div
												key={form.id}
												className="flex items-start justify-between p-4 border rounded-lg"
											>
												<div className="flex items-start gap-3">
													<FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
													<div>
														<div className="flex items-center gap-2">
															<span className="font-medium">{form.name}</span>
															<Badge
																variant={form.required ? "default" : "outline"}
															>
																{form.required ? "Required" : "Optional"}
															</Badge>
														</div>
														{form.description && (
															<p className="text-sm text-muted-foreground mt-1">
																{form.description}
															</p>
														)}
													</div>
												</div>
												<div className="flex items-center gap-1">
													<Button
														variant="ghost"
														size="icon"
														onClick={() => {
															setEditingForm(form);
															setNewFormName(form.name);
															setNewFormDescription(form.description);
															setNewFormRequired(form.required);
															setFormEditorOpen(true);
														}}
													>
														<Pencil className="h-4 w-4" />
													</Button>
													<Button
														variant="ghost"
														size="icon"
														onClick={() => {
															setForms(forms.filter((f) => f.id !== form.id));
															toast.success("Form removed");
														}}
													>
														<Trash2 className="h-4 w-4" />
													</Button>
												</div>
											</div>
										))}
									</div>
								)}

								{forms.length === 0 && (!inheritedOrgForms || inheritedOrgForms.length === 0) && (
									<div className="text-center py-8 text-muted-foreground">
										<FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
										<p>No forms added yet. Create your first form above.</p>
									</div>
								)}
							</CardContent>
						</Card>

						{/* Required Documents */}
						<Card>
							<CardHeader>
								<CardTitle>Required Documents</CardTitle>
								<CardDescription>
									Documents vendors must upload with their submission (not
									fillable forms)
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								{/* Existing required documents list - shown first for better visibility */}
								{requiredDocuments.length > 0 && (
									<div className="space-y-3">
										<Label>
											Document Requirements ({requiredDocuments.length})
										</Label>
										{requiredDocuments.map((doc) => (
											<div
												key={doc.id}
												className="flex items-start justify-between p-4 border rounded-lg bg-background"
											>
												<div className="flex items-start gap-3">
													<Upload className="h-5 w-5 text-muted-foreground mt-0.5" />
													<div>
														<div className="flex items-center gap-2">
															<span className="font-medium">{doc.name}</span>
															<Badge
																variant={doc.required ? "default" : "outline"}
															>
																{doc.required ? "Required" : "Optional"}
															</Badge>
														</div>
														{doc.description && (
															<p className="text-sm text-muted-foreground mt-1">
																{doc.description}
															</p>
														)}
													</div>
												</div>
												<Button
													variant="ghost"
													size="icon"
													onClick={() => {
														setRequiredDocuments(
															requiredDocuments.filter((d) => d.id !== doc.id),
														);
														toast.success("Document requirement removed");
													}}
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</div>
										))}
									</div>
								)}

								{/* Add new required document */}
								<div className="border rounded-lg p-4 space-y-4 bg-muted/30">
									<h4 className="font-medium">Add Document Requirement</h4>
									<div className="grid gap-4 md:grid-cols-2">
										<div className="space-y-2">
											<Label>Document Name *</Label>
											<Input
												placeholder="e.g., Insurance Certificate"
												value={newDocName}
												onChange={(e) => setNewDocName(e.target.value)}
											/>
										</div>
										<div className="space-y-2">
											<Label>Required</Label>
											<div className="flex items-center gap-2 pt-2">
												<Checkbox
													checked={newDocRequired}
													onCheckedChange={(checked) =>
														setNewDocRequired(checked as boolean)
													}
												/>
												<span className="text-sm">
													Vendors must upload this document
												</span>
											</div>
										</div>
									</div>
									<div className="space-y-2">
										<Label>Description</Label>
										<Textarea
											placeholder="Describe what this document should contain..."
											value={newDocDescription}
											onChange={(e) => setNewDocDescription(e.target.value)}
											rows={2}
										/>
									</div>
									<Button onClick={handleAddRequiredDocument}>
										<Plus className="mr-2 h-4 w-4" />
										Add Document Requirement
									</Button>
								</div>

								{requiredDocuments.length === 0 && (
									<div className="text-center py-4 text-muted-foreground">
										<p className="text-sm">No document requirements added yet. Use the form above to add requirements.</p>
									</div>
								)}
							</CardContent>
						</Card>

						{/* Compliance Checks */}
						<Card>
							<CardHeader>
								<CardTitle>Compliance Checks</CardTitle>
								<CardDescription>
									Requirements vendors must meet (certifications, registrations,
									etc.)
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								{/* Inherited checks from organization */}
								{inheritedComplianceChecks.length > 0 && (
									<div className="space-y-3">
										<div className="flex items-center gap-2">
											<Building2 className="h-4 w-4 text-muted-foreground" />
											<Label className="text-muted-foreground">
												Inherited from Organization (
												{inheritedComplianceChecks.length})
											</Label>
										</div>
										{inheritedComplianceChecks.map((check) => (
											<div
												key={check.id}
												className="flex items-start justify-between p-4 border rounded-lg bg-muted/20"
											>
												<div className="flex items-start gap-3">
													<ClipboardCheck className="h-5 w-5 text-muted-foreground mt-0.5" />
													<div>
														<div className="flex items-center gap-2">
															<span className="font-medium">{check.name}</span>
															<Badge
																variant={check.required ? "default" : "outline"}
															>
																{check.required ? "Required" : "Optional"}
															</Badge>
															<Badge variant="secondary">Inherited</Badge>
														</div>
														{check.description && (
															<p className="text-sm text-muted-foreground mt-1">
																{check.description}
															</p>
														)}
													</div>
												</div>
											</div>
										))}
									</div>
								)}

								{/* Add new compliance check */}
								<div className="border rounded-lg p-4 space-y-4 bg-muted/30">
									<h4 className="font-medium">
										Add Solicitation-Specific Compliance Check
									</h4>
									<div className="grid gap-4 md:grid-cols-2">
										<div className="space-y-2">
											<Label>Check Name *</Label>
											<Input
												placeholder="e.g., Security Clearance"
												value={newComplianceName}
												onChange={(e) => setNewComplianceName(e.target.value)}
											/>
										</div>
										<div className="space-y-2">
											<Label>Required</Label>
											<div className="flex items-center gap-2 pt-2">
												<Checkbox
													checked={newComplianceRequired}
													onCheckedChange={(checked) =>
														setNewComplianceRequired(checked as boolean)
													}
												/>
												<span className="text-sm">
													Vendors must meet this requirement
												</span>
											</div>
										</div>
									</div>
									<div className="space-y-2">
										<Label>Description</Label>
										<Textarea
											placeholder="Describe what this compliance check verifies..."
											value={newComplianceDescription}
											onChange={(e) =>
												setNewComplianceDescription(e.target.value)
											}
											rows={2}
										/>
									</div>
									<Button onClick={handleAddComplianceCheck}>
										<Plus className="mr-2 h-4 w-4" />
										Add Compliance Check
									</Button>
								</div>

								{/* Solicitation-specific compliance checks list */}
								{solicitationComplianceChecks.length > 0 && (
									<div className="space-y-3 mt-6">
										<Label>
											Solicitation-Specific Checks (
											{solicitationComplianceChecks.length})
										</Label>
										{solicitationComplianceChecks.map((check) => (
											<div
												key={check.id}
												className="flex items-start justify-between p-4 border rounded-lg"
											>
												<div className="flex items-start gap-3">
													<ClipboardCheck className="h-5 w-5 text-muted-foreground mt-0.5" />
													<div>
														<div className="flex items-center gap-2">
															<span className="font-medium">{check.name}</span>
															<Badge
																variant={check.required ? "default" : "outline"}
															>
																{check.required ? "Required" : "Optional"}
															</Badge>
														</div>
														{check.description && (
															<p className="text-sm text-muted-foreground mt-1">
																{check.description}
															</p>
														)}
													</div>
												</div>
												<Button
													variant="ghost"
													size="icon"
													onClick={() => {
														setSolicitationComplianceChecks(
															solicitationComplianceChecks.filter(
																(c) => c.id !== check.id,
															),
														);
														toast.success("Compliance check removed");
													}}
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</div>
										))}
									</div>
								)}

								{solicitationComplianceChecks.length === 0 &&
									inheritedComplianceChecks.length === 0 && (
										<div className="text-center py-8 text-muted-foreground">
											<ClipboardCheck className="h-12 w-12 mx-auto mb-3 opacity-50" />
											<p>No compliance checks configured.</p>
										</div>
									)}
							</CardContent>
						</Card>

						<PdfFormEditorV2
							open={formEditorOpen}
							onClose={() => {
								setFormEditorOpen(false);
								setEditingForm(null);
								setNewFormName("");
								setNewFormDescription("");
								setNewFormRequired(true);
							}}
							onSave={async (formData) => {
								// Use description from PdfFormEditorV2 or the inline form
								const description = formData.description || newFormDescription;
								// Map widgets to the format we need to store
								const widgetsToStore = formData.widgets.map((w) => ({
									field_name: w.field_name,
									field_label: w.field_label,
									field_type: w.field_type,
									page_number: w.page_number,
									x: w.x,
									y: w.y,
									width: w.width,
									height: w.height,
									is_required: w.is_required,
									placeholder: w.placeholder,
									description: w.description,
									options: w.options,
									font_size: w.font_size,
									tab_order: w.tab_order,
								}));
								if (editingForm) {
									setForms(
										forms.map((f) =>
											f.id === editingForm.id
												? {
														...f,
														name: newFormName || formData.name,
														description,
														required: newFormRequired,
														pdfStoragePath: formData.pdfStoragePath,
														widgets: widgetsToStore,
													}
												: f,
										),
									);
									toast.success("Form updated");
								} else {
									setForms([
										...forms,
										{
											id: crypto.randomUUID(),
											name: newFormName || formData.name,
											description,
											required: newFormRequired,
											pdfStoragePath: formData.pdfStoragePath,
											widgets: widgetsToStore,
										},
									]);
									toast.success("Form added");
								}
								setNewFormName("");
								setNewFormDescription("");
								setNewFormRequired(true);
								setFormEditorOpen(false);
								setEditingForm(null);
							}}
							initialPdfStoragePath={editingForm?.pdfStoragePath || null}
							formName={editingForm?.name || newFormName}
							formDescription={editingForm?.description || newFormDescription}
							organizationId={user?.organizationId || ""}
						/>
					</TabsContent>

					<TabsContent value="evaluation" className="space-y-6">
						{/* Evaluation Rubric */}
						<Card>
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									<Target className="h-5 w-5" />
									Evaluation Rubric
								</CardTitle>
								<CardDescription>
									Define criteria used to evaluate bids. Total points should
									equal 100.
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								{/* Points progress */}
								<div className="mb-4">
									<div className="flex items-center justify-between mb-2">
										<span className="text-sm font-medium">Total Points</span>
										<span
											className={`text-sm font-bold ${totalPoints === 100 ? "text-green-600" : totalPoints > 100 ? "text-red-600" : "text-yellow-600"}`}
										>
											{totalPoints} / 100
										</span>
									</div>
									<Progress
										value={Math.min(totalPoints, 100)}
										className={
											totalPoints === 100
												? "[&>div]:bg-green-500"
												: totalPoints > 100
													? "[&>div]:bg-red-500"
													: "[&>div]:bg-yellow-500"
										}
									/>
								</div>

								{/* Criteria list */}
								<div className="space-y-3">
									{criteria
										.sort((a, b) => a.sortOrder - b.sortOrder)
										.map((criterion) => (
											<div
												key={criterion.id}
												className="flex items-start gap-3 p-4 border rounded-lg bg-muted/30"
											>
												<div className="flex-1">
													<div className="flex items-center justify-between">
														<h4 className="font-medium">{criterion.title}</h4>
														<Badge variant="outline">
															{criterion.maxPoints} pts
														</Badge>
													</div>
													<p className="text-sm text-muted-foreground mt-1">
														{criterion.description}
													</p>
												</div>
												<div className="flex gap-1">
													<Button
														variant="ghost"
														size="icon"
														onClick={() => handleEditCriterion(criterion)}
													>
														<Pencil className="h-4 w-4" />
													</Button>
													<Button
														variant="ghost"
														size="icon"
														className="text-red-500 hover:text-red-700"
														onClick={() => handleDeleteCriterion(criterion.id)}
													>
														<Trash2 className="h-4 w-4" />
													</Button>
												</div>
											</div>
										))}
								</div>

								{/* Add/Edit criterion form */}
								<div className="border rounded-lg p-4 bg-muted/20">
									<h4 className="font-medium mb-3">
										{editingCriterion ? "Edit Criterion" : "Add New Criterion"}
									</h4>
									<div className="grid gap-4 md:grid-cols-2">
										<div className="space-y-2">
											<Label>Title</Label>
											<Input
												value={criterionTitle}
												onChange={(e) => setCriterionTitle(e.target.value)}
												placeholder="e.g., Technical Approach"
											/>
										</div>
										<div className="space-y-2">
											<Label>Max Points</Label>
											<Input
												type="number"
												value={criterionPoints}
												onChange={(e) =>
													setCriterionPoints(Number(e.target.value))
												}
												min={1}
												max={100}
											/>
										</div>
										<div className="space-y-2 md:col-span-2">
											<Label>Description</Label>
											<Textarea
												value={criterionDescription}
												onChange={(e) =>
													setCriterionDescription(e.target.value)
												}
												placeholder="Describe what evaluators should look for..."
												rows={2}
											/>
										</div>
									</div>
									<div className="flex gap-2 mt-4">
										{editingCriterion ? (
											<>
												<Button onClick={handleUpdateCriterion}>
													Update Criterion
												</Button>
												<Button
													variant="outline"
													onClick={() => {
														setEditingCriterion(null);
														setCriterionTitle("");
														setCriterionDescription("");
														setCriterionPoints(10);
													}}
												>
													Cancel
												</Button>
											</>
										) : (
											<Button onClick={handleAddCriterion}>
												<Plus className="mr-2 h-4 w-4" />
												Add Criterion
											</Button>
										)}
									</div>
								</div>
							</CardContent>
						</Card>

						{/* Scoring Weights */}
						{criteria.length > 0 && (
						<Card>
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									<Scale className="h-5 w-5" />
									Scoring Weights
								</CardTitle>
								<CardDescription>
									Adjust the relative importance of each criterion. Total: {totalWeight}%
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								{/* Weight progress */}
								<div className="mb-4">
									<div className="flex items-center justify-between mb-2">
										<span className="text-sm font-medium">Total Weight</span>
										<span
											className={`text-sm font-bold ${totalWeight === 100 ? "text-green-600" : "text-yellow-600"}`}
										>
											{totalWeight}%
										</span>
									</div>
									<Progress
										value={Math.min(totalWeight, 100)}
										className={
											totalWeight === 100
												? "[&>div]:bg-green-500"
												: "[&>div]:bg-yellow-500"
										}
									/>
								</div>

								{/* Weight sliders */}
								<div className="space-y-6">
									{criteria.map((criterion) => (
										<div key={criterion.id} className="space-y-2">
											<div className="flex items-center justify-between">
												<Label>{criterion.title}</Label>
												<span className="text-sm font-medium">
													{criteriaWeights[criterion.id] ?? 0}%
												</span>
											</div>
											<Slider
												value={[criteriaWeights[criterion.id] ?? 0]}
												onValueChange={(value) =>
													handleWeightChange(criterion.id, value[0])
												}
												max={100}
												step={5}
											/>
										</div>
									))}
								</div>
								{totalWeight !== 100 && (
									<p className="text-sm text-amber-600">
										Note: Weights should total 100%. Currently at {totalWeight}%.
									</p>
								)}
							</CardContent>
						</Card>
						)}
					</TabsContent>
				</Tabs>
			</div>
		</DashboardLayout>
	);
}
